import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, Bot, User as UserIcon } from 'lucide-react';
import { api, unwrap } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';

interface Message {
  id: number;
  role: 'user' | 'assistant' | 'system' | 'tool';
  content: string;
  created_at: string;
}

interface Conversation {
  id: string;
  agent_class: string;
  session_id: string | null;
  visitor_ip: string | null;
  context: Record<string, unknown>;
  captured_lead: Record<string, unknown> | null;
  last_message_at: string | null;
  message_count: number;
  messages: Message[];
  leads: Array<{ id: string; name: string | null; email: string | null; status: string }>;
}

export const Route = createFileRoute('/_app/ai/conversations/$id')({
  component: ConversationDetailPage,
});

function ConversationDetailPage() {
  const { id } = Route.useParams();
  const { data, isLoading } = useQuery({
    queryKey: ['ai-conversation', id],
    queryFn: () => unwrap<Conversation>(api.get(`/ai/conversations/${id}`)),
  });

  return (
    <div className="mx-auto max-w-5xl space-y-6 p-6 md:p-8">
      <Link to="/ai/conversations" className="inline-flex items-center gap-1 text-xs text-neutral-500 hover:text-neutral-900">
        <ArrowLeft className="size-3" /> Conversaciones
      </Link>

      <header>
        <h1 className="text-2xl font-bold tracking-tight">Conversación</h1>
        <p className="text-xs font-mono text-neutral-500">{id}</p>
      </header>

      {isLoading && <p className="text-sm text-neutral-500">Cargando…</p>}

      {data && (
        <div className="grid gap-4 lg:grid-cols-[1fr_280px]">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Mensajes ({data.messages.length})</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {data.messages.map((m) => {
                const isUser = m.role === 'user';
                return (
                  <div key={m.id} className={`flex gap-3 ${isUser ? 'flex-row-reverse' : ''}`}>
                    <span className={`grid size-8 shrink-0 place-items-center rounded-full ${
                      isUser ? 'bg-blue-100 text-blue-700' : 'bg-neutral-900 text-white'
                    }`}>
                      {isUser ? <UserIcon className="size-4" /> : <Bot className="size-4" />}
                    </span>
                    <div className={`max-w-[80%] rounded-lg p-3 text-sm ${
                      isUser ? 'bg-blue-50 text-neutral-900' : 'bg-neutral-100 text-neutral-900'
                    }`}>
                      <div className="mb-1 text-[10px] uppercase tracking-wider text-neutral-500">
                        {m.role} · {new Date(m.created_at).toLocaleString('es')}
                      </div>
                      <div className="whitespace-pre-wrap leading-relaxed">{m.content}</div>
                    </div>
                  </div>
                );
              })}
              {data.messages.length === 0 && (
                <p className="text-center text-sm text-neutral-400">Sin mensajes registrados.</p>
              )}
            </CardContent>
          </Card>

          <div className="space-y-4">
            <Card>
              <CardHeader><CardTitle className="text-base">Metadata</CardTitle></CardHeader>
              <CardContent className="space-y-2 text-xs">
                <Meta label="Agente" value={data.agent_class.split('\\').pop() ?? ''} />
                <Meta label="Sesión" value={data.session_id ?? '—'} />
                <Meta label="IP visitante" value={data.visitor_ip ?? '—'} />
                <Meta label="Último mensaje" value={data.last_message_at ? new Date(data.last_message_at).toLocaleString('es') : '—'} />
              </CardContent>
            </Card>

            {data.leads.length > 0 && (
              <Card>
                <CardHeader><CardTitle className="text-base">Leads capturados</CardTitle></CardHeader>
                <CardContent className="space-y-2">
                  {data.leads.map((l) => (
                    <div key={l.id} className="rounded-md border border-neutral-200 p-2 text-sm">
                      <div className="font-medium">{l.name ?? l.email ?? 'Lead'}</div>
                      {l.email && <div className="text-xs text-neutral-500">{l.email}</div>}
                      <Badge variant="outline" className="mt-1 text-[10px]">{l.status}</Badge>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function Meta({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-neutral-500">{label}</div>
      <div className="break-all font-mono text-xs">{value}</div>
    </div>
  );
}
